import * as React from 'react';
import { Separator } from '@/hen-ui/atomics/Separator';
import { Text, type TextProps } from '@/hen-ui/atomics/Text';
import { Flex } from '@/hen-ui/composites/Flex';
import { cn } from '@/hen-ui/utils';

export interface SimpleDataPointProps {
  /** Data point title/label (required, bold text). */
  title: React.ReactNode;
  /** Data value to display (required, bold). */
  value: React.ReactNode;
  /** Optional description text below title. */
  description?: React.ReactNode;
  /** Whether to show white background container with padding and border radius. */
  background?: boolean;
  /** Whether to show bottom border divider (mutually exclusive with background). */
  rowDivider?: boolean;
  /**
   * Type scale for the title host. Defaults to `b4`.
   * Put the intended size here — nested `Text` cannot override the host.
   */
  titleVariant?: TextProps['variant'];
  /**
   * Type scale for the value host. Defaults to `b2`.
   * Put the intended size here — nested `Text` cannot override the host.
   */
  valueVariant?: TextProps['variant'];
  /** Font weight for the value host. Defaults to `bold`. */
  valueWeight?: TextProps['weight'];
  /** Optional className for the container. */
  className?: string;
}

/**
 * SimpleDataPoint - Composite component for displaying a labeled value.
 *
 * Renders a title/description block on the left with a value on the right.
 * Supports optional background container or row divider styling.
 */
export const SimpleDataPoint: React.FC<SimpleDataPointProps> = ({
  title,
  value,
  description,
  background = false,
  rowDivider = false,
  titleVariant = 'b4',
  valueVariant = 'b2',
  valueWeight = 'bold',
  className,
}) => {
  const showBackground = background;
  const showRowDivider = !showBackground && rowDivider;

  return (
    <Flex
      direction='column'
      gap='xxs'
      className={cn(showBackground && 'bg-white p-4 rounded-md', className)}
    >
      <Flex
        direction='row'
        align='start'
        justify='between'
        gap='xxs'
        className={cn(showRowDivider && 'pb-2')}
      >
        <Flex direction='column' gap='xxs' className='min-w-0 gap-1'>
          <Text variant={titleVariant} color='heading' className='font-bold'>
            {title}
          </Text>
          {description && (
            <Text variant='l1' color='default'>
              {description}
            </Text>
          )}
        </Flex>
        <Text
          variant={valueVariant}
          weight={valueWeight}
          color='heading'
          className='min-w-0 flex-1 break-words text-right'
        >
          {value}
        </Text>
      </Flex>
      {showRowDivider && <Separator />}
    </Flex>
  );
};

SimpleDataPoint.displayName = 'SimpleDataPoint';
